import { useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Crown } from "lucide-react";
import Layout from "@/components/Layout";
import CourseModuleRow from "@/components/CourseModuleRow";
import type { Course, CourseModule } from "@/types/course";
import { GET_COURSE_BY_SLUG, MARK_COURSE_MODULE_COMPLETED } from "@/queries/courses";
import { useAuth } from "@/contexts/AuthContext";
import { resolveCourseVideoSource } from "@/lib/courses/resolveCourseVideoSource";
import {
  CourseVideoPlayer,
  type CourseVideoPlayerHandle,
} from "@/components/course/CourseVideoPlayer";
import { LessonResources } from "@/components/course/LessonResources";

const CourseModulePage = () => {
  const { slug, moduleOrder } = useParams<{ slug: string; moduleOrder: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoPlayerRef = useRef<CourseVideoPlayerHandle>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const locale = i18n.language.startsWith("en") ? "en" : "pt-BR";
  const orderNum = parseInt(moduleOrder ?? "", 10);

  const { data, loading, error } = useQuery<{ course: Course | null }>(GET_COURSE_BY_SLUG, {
    variables: { slug: slug ?? "", locale },
    skip: !slug,
  });

  const [markCompleted] = useMutation(MARK_COURSE_MODULE_COMPLETED);

  const course = data?.course ?? null;
  const allModules: CourseModule[] = course
    ? [...course.ungroupedModules, ...course.moduleGroups.flatMap((g) => g.modules)]
    : [];
  const idx = allModules.findIndex((m) => m.order === orderNum);
  const currentModule = idx >= 0 ? allModules[idx] : null;
  const prev = idx > 0 ? allModules[idx - 1] : null;
  const next = idx >= 0 && idx < allModules.length - 1 ? allModules[idx + 1] : null;

  const navigateWithCompletion = async (path: string) => {
    if (
      currentModule &&
      user &&
      !currentModule.isCompleted &&
      !currentModule.isLocked
    ) {
      try {
        await markCompleted({ variables: { moduleId: currentModule.id } });
      } catch {
        // best-effort
      }
    }
    navigate(path);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex-1 p-4 lg:p-6">
          <div className="container mx-auto text-center text-muted-foreground py-12">
            {t("common.loading")}
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !course || !currentModule) {
    return (
      <Layout>
        <div className="flex-1 p-4 lg:p-6">
          <div className="container mx-auto max-w-2xl text-center py-12 space-y-4">
            <p className="text-lg font-semibold">{t("course.notFound")}</p>
            <Link to="/tutorials">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("course.back")}
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const videoSource = !currentModule.isLocked
    ? resolveCourseVideoSource(currentModule.videoUrl)
    : null;

  return (
    <Layout>
      <div className="flex-1 p-4 lg:p-6">
        <div className="container mx-auto max-w-6xl space-y-6">
          {/* Breadcrumb */}
          <button
            onClick={() => navigateWithCompletion(`/courses/${course.slug}`)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {course.title}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            {/* Main column — video + title + prev/next */}
            <div className="space-y-4">
              <h1 className="text-xl lg:text-2xl font-bold leading-tight">
                {currentModule.title}
              </h1>

              {currentModule.isLocked ? (
                <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-primary flex-shrink-0" />
                    <p className="text-base font-semibold">{t("course.subscribePrompt")}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{t("course.subscribeBody")}</p>
                  {currentModule.description && (
                    <p className="max-w-2xl text-sm leading-6 text-foreground/75">
                      {currentModule.description}
                    </p>
                  )}
                  <Button
                    className="bg-gradient-primary text-primary-foreground font-semibold hover:opacity-90 border-0 w-fit"
                    onClick={() => navigate("/plans")}
                  >
                    {t("course.subscribeButton")}
                  </Button>
                </div>
              ) : (
                <div className="sticky top-4 z-20">
                  {videoSource?.kind === "direct" ? (
                    <CourseVideoPlayer
                      ref={videoPlayerRef}
                      src={videoSource.src}
                      title={currentModule.title}
                      poster={currentModule.thumbnailUrl}
                      chapters={currentModule.chapters}
                      transcriptUrl={currentModule.transcriptUrl}
                      onTimeChange={setCurrentTime}
                      onEnded={
                        next
                          ? () =>
                              navigateWithCompletion(
                                `/courses/${course.slug}/${next.order}`
                              )
                          : undefined
                      }
                    />
                  ) : videoSource?.kind === "embed" ? (
                    <div className="aspect-video w-full overflow-hidden rounded-2xl border border-border bg-black">
                      <iframe
                        src={videoSource.src}
                        title={currentModule.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <div className="aspect-video w-full rounded-2xl border border-border bg-muted flex items-center justify-center text-muted-foreground">
                      {t("course.notFound")}
                    </div>
                  )}
                </div>
              )}

              {!currentModule.isLocked && (
                <LessonResources
                  description={currentModule.description}
                  chapters={currentModule.chapters}
                  transcriptUrl={currentModule.transcriptUrl}
                  currentTime={currentTime}
                  onSeek={
                    videoSource?.kind === "direct"
                      ? (seconds) => videoPlayerRef.current?.seekTo(seconds)
                      : undefined
                  }
                />
              )}

              {/* Prev / Next */}
              <div className="flex items-center justify-between gap-3 pt-2">
                <Button
                  variant="outline"
                  disabled={!prev}
                  onClick={() => prev && navigateWithCompletion(`/courses/${course.slug}/${prev.order}`)}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("course.previousLesson")}
                </Button>
                <Button
                  disabled={!next}
                  className="bg-gradient-primary text-primary-foreground font-semibold hover:opacity-90 border-0"
                  onClick={() => next && navigateWithCompletion(`/courses/${course.slug}/${next.order}`)}
                >
                  {t("course.nextLesson")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Sidebar TOC */}
            <aside className="lg:sticky lg:top-6 lg:self-start space-y-4">
              {course.ungroupedModules.length > 0 && (
                <div className="space-y-1.5">
                  {course.ungroupedModules.map((m) => (
                    <CourseModuleRow
                      key={m.id}
                      module={m}
                      active={m.id === currentModule.id}
                      onClick={() => navigateWithCompletion(`/courses/${course.slug}/${m.order}`)}
                    />
                  ))}
                </div>
              )}

              {course.moduleGroups.map((group) => (
                <div key={group.id} className="space-y-2">
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                    {group.label}
                  </h2>
                  <div className="space-y-1.5">
                    {group.modules.map((m) => (
                      <CourseModuleRow
                        key={m.id}
                        module={m}
                        active={m.id === currentModule.id}
                        onClick={() => navigateWithCompletion(`/courses/${course.slug}/${m.order}`)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </aside>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CourseModulePage;
